import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table/index";
import Badge from "../ui/badge/Badge";
import { useState } from "react";
import Pagination from "../ui/Pagination/Pagination";
import PageMeta from "../common/PageMeta";
import PageBreadcrumb from "../common/PageBreadCrumb";
import { useModal } from "../../hooks/useModal";
import { Webhook, useWebhookStore } from "../../stores/useWebhookStore";
import Spinner from "../ui/Spinner/Spinner";
import { FiLink, FiRefreshCw } from "react-icons/fi";
import { format } from "date-fns";
import { getCurrencySymbol, getWebhookTransactionData, getWebhookStatusBadgeColor } from "../../utils/helpers";
import AddWebhookUrl from "../Transactions/AddWebhookUrl";
import { WebhookFilterModal } from "./modals/WebhookFilterModal";
import WebhookDetailsModal from "./modals/WebhookDetailsModal";

export default function Webhooks() {
  const [showFilter, setShowFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [retryingWebhook, setRetryingWebhook] = useState<string | null>(null);
  const itemsPerPage = 10;
  
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [webhookErrors, setWebhookErrors] = useState<Record<string, string>>({});

  const { 
    webhooks, 
    loading, 
    setFilteredWebhooks, 
    loadWebhooks, 
    filteredWebhooks, 
    retryWebhook, 
  } = useWebhookStore();
  
  const close = () => {
    closeModal();
  };

  const handleFilterApply = (filters: {
    status: string;
    fromDate: string;
    toDate: string;
    type: string
  }) => {
    let result = [...webhooks];

    if (filters.status && filters.status !== "all") {
      result = result.filter(
        (webhook) => webhook.status === filters.status
      );
    }

    if (filters.fromDate) {
      const fromDate = new Date(filters.fromDate);
      fromDate.setHours(0, 0, 0, 0);
      result = result.filter((webhook) => new Date(webhook.createdAt) >= fromDate);
    }

    if (filters.toDate) {
      const toDate = new Date(filters.toDate);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter((webhook) => new Date(webhook.createdAt) <= toDate);
    }

      if (filters.type && filters.type !== "all") {
    result = result.filter((webhook) => {
      const transactionData = getWebhookTransactionData(webhook);
      return transactionData.category === filters.type;
    });
  }

    setFilteredWebhooks(result);
    setCurrentPage(1);
  };

  const handleRetryWebhook = async (webhookId: string) => {
    setWebhookErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[webhookId];
      return newErrors;
    });

    setRetryingWebhook(webhookId);
    try {
      await retryWebhook(webhookId);
    } catch (error: any) {
      console.error("Failed to retry webhook:", error);
      setWebhookErrors((prev) => ({
        ...prev,
        [webhookId]: error.message || "Yooo Failed to retry webhook",
      }));

      setTimeout(
        () =>
          setWebhookErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[webhookId];
            return newErrors;
          }),
        3000
      );
    } finally {
      setRetryingWebhook(null);
    }
  };

  const handleOpenDetailsModal = (webhook: Webhook) => {
    setSelectedWebhook(webhook);
    openModal();
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd/MM/yyyy");
  };

  const totalPages = Math.ceil(filteredWebhooks.length / itemsPerPage);
  const paginatedData = filteredWebhooks.slice(
    (currentPage - 1) * itemsPerPage,
    Math.min(currentPage * itemsPerPage, filteredWebhooks.length)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner className="h-10 w-10 text-blue-500" />
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title="Webhooks"
        description="Manage your webhook URLs and view delivery history"
      />
      <PageBreadcrumb pageTitle="Webhooks" />

      {/* Add Webhook URL Section */}
      <div className="mb-6">
        <AddWebhookUrl />
      </div>

      {/* Webhooks History Section */}
      <div className="overflow-hidden w-full rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-4 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
        <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Webhook Delivery History
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={loadWebhooks}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title="Refresh"
            >
              <FiRefreshCw className="w-6 h-6" />
            </button>
            <button
              onClick={() => setShowFilter(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
            >
              Filter
            </button>
            <WebhookFilterModal
              isOpen={showFilter}
              onClose={() => setShowFilter(false)}
              onApply={handleFilterApply}
            />
          </div>
        </div>

        {filteredWebhooks.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center py-8 text-gray-500 border-b border-gray-100 dark:border-gray-800">
            <FiLink className="w-12 h-12 mb-4" />
            <p className="text-lg mb-2">No webhooks found</p>
            <p className="text-sm mb-4">Webhook deliveries will appear here</p>
            <button
              onClick={() => setShowFilter(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
            >
              Adjust Filters
            </button>
          </div>
        )}

        <div className="max-w-full overflow-x-auto">
          <div className="hidden sm:block">
            <Table>
              <TableHeader className="border-gray-100 dark:border-gray-800 border-y">
                <TableRow>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Amount
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Type
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Date
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Status
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Retries
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHeader>

              <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
                {paginatedData.map((webhook) => {
                  const transactionData = getWebhookTransactionData(webhook);
                  return (
                    <TableRow key={webhook._id}>
                      <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                        {getCurrencySymbol(
                          (transactionData.paymentDetails?.currency ||
                            transactionData.fromCurrency) as string,
                        )}
                        {transactionData.amount?.toLocaleString()}
                      </TableCell>
                      <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                        <div className="flex items-center gap-2">
                          <Badge
                            size="sm"
                            color={
                              transactionData.category === "payment_link" ? "primary" : 
                              transactionData.category === "exchange" ? "info" : 
                              transactionData.category === "wallet_creation" ? "dark" : 
                              transactionData.category === "wallet_txn" ? "light" : 
                              transactionData.category === "stable_wallet_creation" ? "dark" :
                              transactionData.category === "stable_wallet_txn" ? "light" : 
                              transactionData.category === "wallet_virtual_account" ? "info" :
                              "light"
                            }
                          >
                            { transactionData.category === "payment_link" ? "Payment Link" : 
                              transactionData.category === "exchange" ? "Exchange" : 
                              transactionData.category === "wallet_creation" ? "Wallet Creation" : 
                              transactionData.category === "wallet_txn" ? "Wallet Transaction" : 
                              transactionData.category === "stable_wallet_creation" ? "Stable Wallet Creation" :
                              transactionData.category === "stable_wallet_txn" ? "Stable Wallet Transaction" : 
                              transactionData.category === "wallet_virtual_account" ? "Wallet Virtual Account" :
                              "Unknown"
                            }
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                        {formatDate(webhook.createdAt)}
                      </TableCell>
                      <TableCell className="py-3">
                        <Badge
                          size="sm"
                          color={getWebhookStatusBadgeColor(webhook.status)}
                        >
                          {webhook.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                        {webhook.trialCount}
                      </TableCell>
                      <TableCell className="py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleOpenDetailsModal(webhook)}
                            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            title="View webhook details"
                          >
                            View
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="py-3">
                        <div className="flex items-center gap-2">
                          {webhook.status === "FAILED" && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleRetryWebhook(webhook._id)}
                                className={`
            inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-200
            ${
              retryingWebhook === webhook._id
                ? "bg-blue-100 text-blue-700 cursor-not-allowed"
                : webhookErrors[webhook._id]
                  ? "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800 cursor-not-allowed"
                  : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800 dark:hover:bg-blue-900/40"
            }
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
                                disabled={
                                  retryingWebhook === webhook._id ||
                                  !!webhookErrors[webhook._id]
                                }
                                title={
                                  webhookErrors[webhook._id]
                                    ? `Retry failed: ${
                                        webhookErrors[webhook._id]
                                      }`
                                    : "Retry webhook"
                                }
                              >
                                {retryingWebhook === webhook._id ? (
                                  <>
                                    <svg
                                      className="w-3 h-3 animate-spin"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      ></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      ></path>
                                    </svg>
                                    Retrying...
                                  </>
                                ) : webhookErrors[webhook._id] ? (
                                  <>
                                    <svg
                                      className="w-3 h-3"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    Error
                                  </>
                                ) : (
                                  <>
                                    <svg
                                      className="w-3 h-3"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                      />
                                    </svg>
                                    Retry
                                  </>
                                )}
                              </button>

                              {webhookErrors[webhook._id] && (
                                <div className="relative group">
                                  <svg
                                    className="w-4 h-4 text-red-500 cursor-help"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                                    {webhookErrors[webhook._id]}{" "}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Mobile View */}
          <div className="sm:hidden">
            <Table>
              <TableHeader className="border-gray-100 dark:border-gray-800 border-y">
                <TableRow>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Amount
                  </TableCell>
                  {/* <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Value
                  </TableCell> */}
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Status
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHeader>

              <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
                {paginatedData.map((webhook) => {
                  const transactionData = getWebhookTransactionData(webhook);
                  return (
                    <TableRow key={webhook._id}>
                      <TableCell className="py-3">
                        <div className="text-gray-500 text-theme-sm dark:text-gray-400">
                           {getCurrencySymbol(
                          (transactionData.paymentDetails?.currency ||
                            transactionData.fromCurrency) as string
                        )}
                        {transactionData.amount?.toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell className="py-3">
                        <Badge
                          size="sm"
                          color={getWebhookStatusBadgeColor(webhook.status)}
                        >
                          {webhook.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleOpenDetailsModal(webhook)}
                            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          >
                            View
                          </button>
                          {webhook.status === "FAILED" && (
                            <button
                              onClick={() => handleRetryWebhook(webhook._id)}
                              disabled={retryingWebhook === webhook._id}
                              className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50 text-sm"
                            >
                              {retryingWebhook === webhook._id ? "..." : "Retry"}
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        {filteredWebhooks.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      <WebhookDetailsModal
        isOpen={isOpen}
        onClose={close}
        webhook={selectedWebhook}
      />
    </>
  );
}